import axios from 'axios'
import React, {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {TELEMETRY_DATA_TYPE_NAME} from '../../../../constants/StatusConstants'
import {CriticalityValueModel} from '../../../../entities/component.entity'
import {CriticalitySettingsModel} from '../../../../entities/entities'
import {useReduxAuth} from '../../../auth'
import {useParams} from 'react-router-dom'
import ConfirmationModal from '../modals/ConfirmationModal'
import EditCriticalitySettingsModal from '../modals/EditCriticalitySettingsModal'
import PrimaryButton from '../common/buttons/PrimaryButton'
import './PatientAlertsSettings.css'
import {USER_ROLES} from '../../../../constants/StatusConstants'
import {useMADoctorDropdown} from '../../context/MADoctorDropdownContext'
import APP_TIMEZONE from '../../../../config/timezone'

// Enhanced range configuration with medical standards
interface RangeConfig {
  id: string
  label: string
  color: string
  description: string
  systolicRange: [number, number]
  diastolicRange: [number, number]
  pulseRange: [number, number]
}

const MEDICAL_RANGES: RangeConfig[] = [
  {
    id: 'low',
    label: 'Low',
    color: '#17a2b8',
    description: 'Hypotension - Low blood pressure',
    systolicRange: [0, 90],
    diastolicRange: [0, 60], 
    pulseRange: [40, 60]
  },
  {
    id: 'normal',
    label: 'Normal',
    color: '#28a745',
    description: 'Normal blood pressure range',
    systolicRange: [91, 119],
    diastolicRange: [61, 79],
    pulseRange: [61, 80]
  },
  {
    id: 'elevated',
    label: 'Elevated',
    color: '#ffc107',
    description: 'Elevated blood pressure',
    systolicRange: [120, 129],
    diastolicRange: [61, 79],
    pulseRange: [81, 90]
  },
  {
    id: 'stage1',
    label: 'Hypertension Stage 1',
    color: '#fd7e14',
    description: 'Stage 1 High Blood Pressure',
    systolicRange: [130, 139],
    diastolicRange: [80, 89],
    pulseRange: [91, 100]
  },
  {
    id: 'stage2',
    label: 'Hypertension Stage 2',
    color: '#dc3545',
    description: 'Stage 2 High Blood Pressure',
    systolicRange: [140, 179],
    diastolicRange: [90, 119],
    pulseRange: [101, 120]
  },
  {
    id: 'crisis',
    label: 'Hypertensive Crisis',
    color: '#8B0000',
    description: 'Seek immediate medical attention',
    systolicRange: [180, 250],
    diastolicRange: [120, 150],
    pulseRange: [121, 200]
  }
]

const PatientAlertsSettings = () => {
  // toast alerts
  const notifyError = (msg: string) => toast.error(msg)
  const notifySuccess = (msg: string) => toast.success(msg)

  const {currentUser} = useReduxAuth()
  const {patientId} = useParams()
  const roleId = currentUser?.role_id
  
  // MA Doctor Dropdown
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = roleId === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined

  const [isSettingsAdded, setIsSettingsAdded] = useState(false)
  const [isCreatePatientCriticalityLoading, setIsCreatePatientCriticalityLoading] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const [isEditCriticalitySettingModalOpen, setIsEditCriticalitySettingModalOpen] = useState(false)
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false)

  const [bloodPressureSettings, setBloodPressureSettings] = useState<CriticalitySettingsModel[]>([])
  const [heartRateSettings, setHeartRateSettings] = useState<CriticalitySettingsModel[]>([])
  const [isGeneralSetting, setIsGeneralSetting] = useState(false)

  // Enhanced state for managing ranges
  const [customRanges, setCustomRanges] = useState<RangeConfig[]>(MEDICAL_RANGES)
  const [activeTab, setActiveTab] = useState<'systolic' | 'diastolic' | 'pulse'>('systolic')

  // Local storage key for persisting custom ranges
  const getStorageKey = (patientId: string) => `patient_alert_ranges_${patientId}`

  // Save custom ranges to localStorage
  const saveCustomRangesToStorage = (ranges: RangeConfig[]) => {
    if (patientId) {
      try {
        localStorage.setItem(getStorageKey(patientId), JSON.stringify(ranges))
      } catch (error) {
        console.warn('Failed to save custom ranges to localStorage:', error)
      }
    }
  }

  // Load custom ranges from localStorage
  const loadCustomRangesFromStorage = (): RangeConfig[] | null => {
    if (patientId) {
      try {
        const stored = localStorage.getItem(getStorageKey(patientId))
        return stored ? JSON.parse(stored) : null
      } catch (error) {
        console.warn('Failed to load custom ranges from localStorage:', error)
        return null
      }
    }
    return null
  }

  // Update setCustomRanges to also save to localStorage
  const updateCustomRanges = (ranges: RangeConfig[]) => {
    setCustomRanges(ranges)
    saveCustomRangesToStorage(ranges)
  }

const handleResetToDefault = async () => {
  try {
    // Remove custom ranges from localStorage
    if (patientId) {
      localStorage.removeItem(getStorageKey(patientId))
    }

    // Reset to default medical ranges (deep clone)
    const defaultRanges = JSON.parse(JSON.stringify(MEDICAL_RANGES))
    
    // Update state immediately
    setCustomRanges(defaultRanges)
    
    // Save default ranges to localStorage
    saveCustomRangesToStorage(defaultRanges)

    toast.success('Ranges reset to default values!')
  } catch (error) {
    console.error('Failed to reset to default ranges:', error)
    toast.error('Failed to reset to default values.')
  }
}

  const [currentValues, setCurrentValues] = useState<{
    sysValues: CriticalityValueModel[]
    diaValues: CriticalityValueModel[]
    pulseValues: CriticalityValueModel[]
  }>({
    sysValues: [],
    diaValues: [],
    pulseValues: [],
  })

  useEffect(() => {
    fetchCriticalitySettings()
  }, [])

  // Load custom ranges from localStorage on component mount
  useEffect(() => {
    const storedRanges = loadCustomRangesFromStorage()
    if (storedRanges) {
      setCustomRanges(storedRanges)
    }
  }, [patientId])

  // Enhanced range validation and overlap prevention
  const validateRanges = (ranges: RangeConfig[], type: 'systolic' | 'diastolic' | 'pulse'): boolean => {
    const sortedRanges = [...ranges].sort((a, b) => {
      const aRange = type === 'systolic' ? a.systolicRange : type === 'diastolic' ? a.diastolicRange : a.pulseRange
      const bRange = type === 'systolic' ? b.systolicRange : type === 'diastolic' ? b.diastolicRange : b.pulseRange
      return aRange[0] - bRange[0]
    })

    for (let i = 0; i < sortedRanges.length - 1; i++) {
      const currentRange = type === 'systolic' ? sortedRanges[i].systolicRange : 
                          type === 'diastolic' ? sortedRanges[i].diastolicRange : sortedRanges[i].pulseRange
      const nextRange = type === 'systolic' ? sortedRanges[i + 1].systolicRange : 
                        type === 'diastolic' ? sortedRanges[i + 1].diastolicRange : sortedRanges[i + 1].pulseRange
      
      // Check for gaps or overlaps
      if (currentRange[1] + 1 !== nextRange[0]) {
        return false
      }
    }
    return true
  }

  const adjustRangeToPreventOverlap = (
    ranges: RangeConfig[],
    changedRangeId: string,
    newValue: number,
    isMax: boolean,
    type: 'systolic' | 'diastolic' | 'pulse'
  ): RangeConfig[] => {
    const updatedRanges = [...ranges]
    const changedIndex = updatedRanges.findIndex(r => r.id === changedRangeId)
    
    if (changedIndex === -1) return ranges

    const currentRange = updatedRanges[changedIndex]
    const rangeKey = type === 'systolic' ? 'systolicRange' : type === 'diastolic' ? 'diastolicRange' : 'pulseRange'
    
    if (isMax) {
      // Basic validation: ensure max > min within the same range
      if (newValue <= currentRange[rangeKey][0]) {
        toast.error(`Maximum value must be greater than minimum value (${currentRange[rangeKey][0]}) for ${currentRange.label}`)
        return ranges
      }
      
      currentRange[rangeKey][1] = newValue
      
      // Auto-adjust the next range if it exists
      if (changedIndex < updatedRanges.length - 1) {
        const nextRange = updatedRanges[changedIndex + 1]
        const proposedNextMin = newValue + 1
        
        // Auto-adjust next range's minimum
        nextRange[rangeKey][0] = proposedNextMin
        
        // If the new min exceeds the current max, auto-expand the max
        if (proposedNextMin >= nextRange[rangeKey][1]) {
          nextRange[rangeKey][1] = proposedNextMin + Math.max(10, Math.floor((proposedNextMin) * 0.1)) // Add 10% or minimum 10 points
          
          // Recursively adjust subsequent ranges if needed
          if (changedIndex < updatedRanges.length - 2) {
            const adjustedRanges = adjustRangeToPreventOverlap(updatedRanges, nextRange.id, nextRange[rangeKey][1], true, type)
            return adjustedRanges
          }
        }
      }
    } else {
      // Basic validation: ensure min < max within the same range
      if (newValue >= currentRange[rangeKey][1]) {
        toast.error(`Minimum value must be less than maximum value (${currentRange[rangeKey][1]}) for ${currentRange.label}`)
        return ranges
      }
      
      currentRange[rangeKey][0] = newValue
      
      // Auto-adjust the previous range if it exists
      if (changedIndex > 0) {
        if (newValue === 0) {
          for (let i = 0; i < changedIndex; i++) {
            updatedRanges[i][rangeKey] = [0, 0]
          }
          return updatedRanges
        }

        const prevRange = updatedRanges[changedIndex - 1]
        const proposedPrevMax = newValue - 1

        // Auto-adjust previous range's maximum
        prevRange[rangeKey][1] = proposedPrevMax

        // If the new max is less than the current min, auto-adjust the min
        if (proposedPrevMax <= prevRange[rangeKey][0]) {
          const newMin = Math.max(
            0,
            proposedPrevMax - Math.max(10, Math.floor(proposedPrevMax * 0.1))
          )
          prevRange[rangeKey][0] = newMin

          // Recursively adjust previous ranges if needed
          if (changedIndex > 1) {
            const adjustedRanges = adjustRangeToPreventOverlap(
              updatedRanges,
              prevRange.id,
              newMin,
              false,
              type
            )
            return adjustedRanges
          }
        }
      }
    }

    // Final validation: ensure all ranges are valid and show success message
    let allValid = true
    for (let i = 0; i < updatedRanges.length; i++) {
      const range = updatedRanges[i]
      const rangeValues = range[rangeKey]
      if (rangeValues[0] >= rangeValues[1]) {
        allValid = false
        break
      }
    }

    if (allValid) {
      toast.success(`${currentRange.label} range updated successfully!`)
    }

    return updatedRanges
  }

  const fetchCriticalitySettings = async () => {
    try {
      const isNurse = currentUser?.isNurse
      // Build query params - include doctor_id for MA and timezone
      const params: string[] = []
      if (isNurse) {
        params.push('is_nurse=1')
      }
      if (roleId === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId) {
        params.push(`doctor_id=${maSelectedDoctorId}`)
      }
      // Add timezone (required for MA, optional for others)
      params.push(`timezone=${encodeURIComponent(APP_TIMEZONE)}`)
      const queryParams = params.length > 0 ? params.join('&') : null
      const response = await axios.get(
        API_ENDPOINTS.GET_ALL_PATIENT_CRITICALITY_SETTINGS(patientId!, queryParams)
      )

      const allSettings: CriticalitySettingsModel[] = response.data?.data?.allPatientSettings || []

      // Explicitly define array types
      const bpSettings: CriticalitySettingsModel[] = []
      const hrSettings: CriticalitySettingsModel[] = []

      allSettings?.forEach((setting) => {
        const type = setting.TelemetryType?.name
        if (
          type === TELEMETRY_DATA_TYPE_NAME.SYSTOLIC_PRESSURE ||
          type === TELEMETRY_DATA_TYPE_NAME.DIASTOLIC_PRESSURE
        ) {
          bpSettings.push(setting)
        } else if (type === TELEMETRY_DATA_TYPE_NAME.PULSE) {
          hrSettings.push(setting)
        }
      })

      setBloodPressureSettings(bpSettings)
      setHeartRateSettings(hrSettings)
      setIsGeneralSetting(response?.data?.data?.isGeneralSettings)

      // Update currentValues with mapped values
      if (allSettings?.length > 0) {
        setCurrentValues({
          sysValues: mapCriticalityValues(bpSettings, TELEMETRY_DATA_TYPE_NAME.SYSTOLIC_PRESSURE),
          diaValues: mapCriticalityValues(bpSettings, TELEMETRY_DATA_TYPE_NAME.DIASTOLIC_PRESSURE),
          pulseValues: mapCriticalityValues(hrSettings, TELEMETRY_DATA_TYPE_NAME.PULSE),
        })
        
        // Sync custom ranges with API data if available
        syncCustomRangesWithApiData(bpSettings)
        setIsSettingsAdded(true)
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || 'Something went wrong')
      } else if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('An unexpected error occurred')
      }
    }
  }

  // Sync custom ranges with actual API data
  const syncCustomRangesWithApiData = (bpSettings: CriticalitySettingsModel[]) => {
    const systolicSettings = bpSettings.filter(
      s => s.TelemetryType?.name === TELEMETRY_DATA_TYPE_NAME.SYSTOLIC_PRESSURE
    )
    const diastolicSettings = bpSettings.filter(
      s => s.TelemetryType?.name === TELEMETRY_DATA_TYPE_NAME.DIASTOLIC_PRESSURE
    )

    if (systolicSettings.length && diastolicSettings.length) {
      const apiSysMin = Math.min(...systolicSettings.map(s => s.min_value))
      const apiSysMax = Math.max(...systolicSettings.map(s => s.max_value))
      const apiDiaMin = Math.min(...diastolicSettings.map(s => s.min_value))
      const apiDiaMax = Math.max(...diastolicSettings.map(s => s.max_value))

      // First, try to load saved custom ranges from localStorage
      const storedRanges = loadCustomRangesFromStorage()

      if (storedRanges) {
        // Check if stored ranges match API boundaries
        const storedSysMin = storedRanges[0]?.systolicRange[0]
        const storedSysMax = storedRanges[storedRanges.length - 1]?.systolicRange[1]
        const storedDiaMin = storedRanges[0]?.diastolicRange[0]
        const storedDiaMax = storedRanges[storedRanges.length - 1]?.diastolicRange[1]

        if (
          storedSysMin === apiSysMin && storedSysMax === apiSysMax &&
          storedDiaMin === apiDiaMin && storedDiaMax === apiDiaMax
        ) {
          setCustomRanges(storedRanges)
          return
        }
      }

      // No stored ranges or incompatible, create default ranges with API boundaries
      const updatedRanges = [...MEDICAL_RANGES]

      updatedRanges[0].systolicRange = [apiSysMin, updatedRanges[0].systolicRange[1]]
      updatedRanges[0].diastolicRange = [apiDiaMin, updatedRanges[0].diastolicRange[1]]

      updatedRanges[updatedRanges.length - 1].systolicRange = [
        updatedRanges[updatedRanges.length - 1].systolicRange[0],
        apiSysMax,
      ]
      updatedRanges[updatedRanges.length - 1].diastolicRange = [
        updatedRanges[updatedRanges.length - 1].diastolicRange[0],
        apiDiaMax,
      ]

      updateCustomRanges(updatedRanges)
    }
  }

  // This is used to map values so we can display it in the cards
  const mapCriticalityValues = (settings: CriticalitySettingsModel[], type: string) => {
    const mappedSettings = settings
      .filter((setting) => setting.TelemetryType?.name === type)
      .flatMap((setting) => [
        {id: setting.id, value: setting.min_value, is_max_value: false},
        {id: setting.id, value: setting.max_value, is_max_value: true},
      ])

    const reshaped = [mappedSettings[0], mappedSettings[2], mappedSettings[3], mappedSettings[1]]

    return reshaped
  }

  const handleDeletePatientSettings = async () => {
    try {
      const isNurse = currentUser?.isNurse
      // Build query params - include doctor_id for MA and timezone
      const params: string[] = []
      if (isNurse) {
        params.push('is_nurse=1')
      }
      if (roleId === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId) {
        params.push(`doctor_id=${maSelectedDoctorId}`)
      }
      // Add timezone (required for MA, optional for others)
      params.push(`timezone=${encodeURIComponent(APP_TIMEZONE)}`)
      const queryParams = params.length > 0 ? params.join('&') : null

      const response = await axios.delete(
        API_ENDPOINTS.GET_ALL_PATIENT_CRITICALITY_SETTINGS(patientId!, queryParams)
      )

      if (response.data.status === 'Success') {
        toast.success(response.data.message)
        // Clear custom ranges from localStorage when settings are deleted
        if (patientId) {
          localStorage.removeItem(getStorageKey(patientId))
        }
      } else {
        toast.error(response.data.message || 'Failed to delete criticality settings.')
      }
      setOpenConfirmationModal(false)
      fetchCriticalitySettings()
    } catch (error) {
      // Todo Refactor this
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || 'Something went wrong')
      } else if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('An unexpected error occurred')
      }
    }
  }

  const handleSubmitSettings = async (values: {
    sysValues: CriticalityValueModel[]
    diaValues: CriticalityValueModel[]
    pulseValues: CriticalityValueModel[]
  }) => {
    if (isGeneralSetting) {
      sendCreateRequestSettings(values)
    } else {
      sendEditRequestSettings(values)
    }
  }

  const sendCreateRequestSettings = async (values: any) => {
    try {
      const valuesReshaped = reshapeData(values)

      const response = await axios.post(API_ENDPOINTS.CREATE_NEW_CRITICALITY_SETTINGS(), {
        settings_data: valuesReshaped,
        is_nurse: currentUser?.isNurse ? true : null,
        patient_uuid: patientId,
      })
      if (response.data.status === 'Success') {
        toast.success(response.data.message)
      } else {
        toast.error(response.data.message || 'Failed to update criticality settings.')
      }
      setIsEditCriticalitySettingModalOpen(false)
      fetchCriticalitySettings()
    } catch (error) {
      // Todo Refactor this
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || 'Something went wrong')
      } else if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('An unexpected error occurred')
      }
    }
  }

  const sendEditRequestSettings = async (values: any) => {
    try {
      const settingsData = [...values?.sysValues, ...values?.diaValues, ...values?.pulseValues]

      const response = await axios.put(API_ENDPOINTS.CREATE_NEW_CRITICALITY_SETTINGS(), {
        settings_data: settingsData,
        is_nurse: currentUser?.isNurse ? true : null,
      })

      if (response.data.status === 'Success') {
        toast.success(response.data.message)
      } else {
        toast.error(response.data.message || 'Failed to update criticality settings.')
      }
      setIsEditCriticalitySettingModalOpen(false)
      fetchCriticalitySettings()
    } catch (error) {
      // Todo Refactor this
      if (axios.isAxiosError(error)) {
        toast.error(error.response?.data?.message || 'Something went wrong')
      } else if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('An unexpected error occurred')
      }
    }
  }

  const reshapeData = (values: any) => {
    // Reassign values and combining save severity values together
    return {
      systolic_pressure: {
        critical: {
          min: values.sysValues[0]?.value,
          max: values.sysValues[3]?.value,
        },
        warning: {
          min: values.sysValues[1]?.value,
          max: values.sysValues[2]?.value,
        },
      },
      diastolic_pressure: {
        critical: {
          min: values.diaValues[0]?.value,
          max: values.diaValues[3]?.value,
        },
        warning: {
          min: values.diaValues[1]?.value,
          max: values.diaValues[2]?.value,
        },
      },
      pulse: {
        critical: {
          min: values.pulseValues[0]?.value,
          max: values.pulseValues[3]?.value,
        },
        warning: {
          min: values.pulseValues[1]?.value,
          max: values.pulseValues[2]?.value,
        },
      },
    }
  }

  const handleUpdateCriticalitySettings = async (selectedRowData: any) => {
    try {
      const response = await axios.put(API_ENDPOINTS.CREATE_NEW_CRITICALITY_SETTINGS(), {
        systolic: selectedRowData.systolic,
        diastolic: selectedRowData.diastolic,
        is_nurse: selectedRowData.is_nurse,
        patient_uuid: selectedRowData.patient_uuid,
      })

      // Check for success message in the response
      if (response.status === 200 && response.data.message === 'Success') {
        notifySuccess('Settings updated successfully')
      } else {
        notifyError(response.data.message || 'Failed to update settings')
      }
      
      fetchCriticalitySettings()
    } catch (error: any) {
      notifyError(error?.response?.data?.message || 'Failed to update settings')
    }
  }

  const createPatientCriticalitySettings = async () => {
    setIsCreatePatientCriticalityLoading(true)
    const isNurse = currentUser?.isNurse ? true : null
    try {
      // Build query params - include doctor_id for MA
      const doctorIdParam = roleId === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId
        ? `doctor_id=${maSelectedDoctorId}`
        : null
      const response = await axios.post(
        API_ENDPOINTS.CREATE_PATIENT_CRITICALITY_SETTINGS(patientId!, isNurse, doctorIdParam)
      )
      fetchCriticalitySettings()
    } catch (error: any) {
      notifyError(error?.response?.data?.message)
    } finally {
      setIsCreatePatientCriticalityLoading(false)
    }
  }

  // this will make sure that the settings are created when the component mounts
  // and if the settings are not created, it will create them
  useEffect(() => {
    if (bloodPressureSettings?.length < 1 ) {
      createPatientCriticalitySettings()
      }
    }, [])

  // Enhanced UI Component for Range Management
  const RangeCard = ({ range, type }: { range: RangeConfig; type: 'systolic' | 'diastolic' | 'pulse' }) => {
    const currentRange = type === 'systolic' ? range.systolicRange : 
                        type === 'diastolic' ? range.diastolicRange : range.pulseRange
    
    const unit = type === 'pulse' ? 'bpm' : 'mmHg'
    
    // Local state for input values to allow free typing
    const [minInput, setMinInput] = useState(currentRange[0].toString())
    const [maxInput, setMaxInput] = useState(currentRange[1].toString())
    
    // Update local state when range changes externally
    React.useEffect(() => {
      setMinInput(currentRange[0].toString())
      setMaxInput(currentRange[1].toString())
    }, [currentRange[0], currentRange[1]])
    
    const handleMinBlur = () => {
      const newValue = parseInt(minInput)
      if (!isNaN(newValue) && newValue !== currentRange[0]) {
        handleRangeChange(range.id, newValue, false, type)
      } else {
        // Reset to current value if invalid
        setMinInput(currentRange[0].toString())
      }
    }
    
    const handleMaxBlur = () => {
      const newValue = parseInt(maxInput)
      if (!isNaN(newValue) && newValue !== currentRange[1]) {
        handleRangeChange(range.id, newValue, true, type)
      } else {
        // Reset to current value if invalid
        setMaxInput(currentRange[1].toString())
      }
    }
    
    const handleKeyPress = (e: React.KeyboardEvent, isMax: boolean) => {
      if (e.key === 'Enter') {
        if (isMax) {
          handleMaxBlur()
        } else {
          handleMinBlur()
        }
      }
    }
    
    return (
      <div className="card mb-3" style={{ borderLeft: `4px solid ${range.color}` }}>
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="card-title mb-0" style={{ color: range.color }}>
              {range.label}
            </h6>
            <span className="badge" style={{ backgroundColor: range.color, color: 'white' }}>
              {range.id === 'low' ? 
                `<= ${currentRange[1]} ${unit}` : 
                range.id === 'crisis' ? 
                  `>= ${currentRange[0]} ${unit}` : 
                  `${currentRange[0]} - ${currentRange[1]} ${unit}`
              }
            </span>
          </div>
          <p className="card-text text-muted small mb-3">{range.description}</p>
          
          {isEditMode && (
            <div className="row">
              {/* {range.id !== 'low' && ( */}
                <div className="col-6">
                  <label className="form-label small">Min Value</label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    value={minInput}
                    onChange={(e) => setMinInput(e.target.value)}
                    onBlur={handleMinBlur}
                    onKeyPress={(e) => handleKeyPress(e, false)}
                    min="0"
                    max="300"
                    placeholder="Min value"
                  />
                  {/* {range.id !== 'crisis' && (
                    <small className="text-muted">Must be less than {currentRange[1]}</small>
                  )} */}
                </div>
              {/* )} */}
              {/* {range.id !== 'crisis' && ( */}
                <div className={"col-6"}>
                  <label className="form-label small">Max Value</label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    value={maxInput}
                    onChange={(e) => setMaxInput(e.target.value)}
                    onBlur={handleMaxBlur}
                    onKeyPress={(e) => handleKeyPress(e, true)}
                    min="0"
                    max="300"
                    placeholder="Max value"
                  />
                  {/* <small className="text-muted">Must be greater than {currentRange[0]}</small> */}
                </div>
              {/* )} */}
            </div>
          )}
        </div>
      </div>
    )
  }

  const handleRangeChange = (rangeId: string, newValue: number, isMax: boolean, type: 'systolic' | 'diastolic' | 'pulse') => {
    // Basic boundary checks
    if (newValue < 0 || newValue > 300) {
      toast.error(`Value must be between 0 and 300`)
      return
    }

    const updatedRanges = adjustRangeToPreventOverlap(customRanges, rangeId, newValue, isMax, type)
    updateCustomRanges(updatedRanges)
  }

  const handleSaveChanges = async () => {
    setIsSaving(true)
    try {
      // Convert custom ranges back to the correct API format
      const systolicData = customRanges.map(range => ({
        label: range.label, // e.g. "Low", "Normal"
        min: range.systolicRange[0],
        max: range.systolicRange[1],
      }))

      const diastolicData = customRanges.map(range => ({
      label: range.label,
      min: range.diastolicRange[0],
      max: range.diastolicRange[1],
    }))

      const payload = {
        systolic: systolicData,
        diastolic: diastolicData,
        is_nurse: currentUser?.isNurse ? true : null,
        patient_uuid: patientId
      }

      // Call the API directly to have better control over response handling
      const response = await axios.put(API_ENDPOINTS.CREATE_NEW_CRITICALITY_SETTINGS(), payload)
      
      // Check for success message in the response
      if (response.status === 200 && response.data.message === 'Success') {
        notifySuccess('Alert settings updated successfully!')
        setIsEditMode(false)
        fetchCriticalitySettings()
      } else {
        notifyError(response.data.message || 'Failed to update settings')
      }
    } catch (error: any) {
      console.error('Error updating settings:', error)
      notifyError(error?.response?.data?.message || 'Failed to save settings. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const TabContent = ({ type }: { type: 'systolic' | 'diastolic' | 'pulse' }) => {
    const title = type === 'systolic' ? 'Systolic Blood Pressure' : 
                  type === 'diastolic' ? 'Diastolic Blood Pressure' : 'Pulse Rate'
    
    return (
      <div className="tab-pane fade show active">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="mb-0">{title}</h5>
          {bloodPressureSettings?.length > 0 && (
            <div>
              {!isEditMode ? (
                <button 
                  className="btn btn-primary btn-sm"
                  onClick={() => setIsEditMode(true)}
                >
                  <i className="fas fa-edit me-2"></i>Edit Settings
                </button>
              ) : (
                <div className="btn-group">
                  <button 
                    className="btn btn-success btn-sm"
                    onClick={handleSaveChanges}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>Save Changes
                      </>
                    )}
                  </button>
                  <button 
                    className="btn btn-warning btn-sm"
                    onClick={handleResetToDefault}
                    title="Reset to default medical ranges"
                  >
                    <i className="fas fa-undo me-2"></i>Reset Default
                  </button>
                  <button 
                    className="btn btn-secondary btn-sm text-white"
                    onClick={() => {
                      setIsEditMode(false)
                      // Reload from storage or API to revert changes
                      fetchCriticalitySettings()
                    }}
                  >
                    <i className="fas fa-times me-2 text-white"></i>Cancel
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="row">
          {customRanges.map((range) => (
            <div key={range.id} className="col-md-6 col-lg-4">
              <RangeCard range={range} type={type} />
            </div>
          ))}
        </div>
        
        {isEditMode && (
            <div className="alert alert-warning mt-2 mb-0">
              <small><strong>Tip:</strong> If ranges become invalid due to cascading changes, use the "Reset Default" button to restore medical standard ranges.</small>
            </div>
        )}
      </div>
    )
  }

  return (
    <div className='card mb-5 mb-xl-10' id='kt_profile_details_view'>
      <div className='card-header d-flex justify-content-between align-items-center'>
        <h3 className='fw-bolder m-0'>Patient Alert Settings</h3>

        {bloodPressureSettings?.length < 1 && (
          <PrimaryButton
            disabled={isCreatePatientCriticalityLoading}
            className='mx-4'
            onClick={() => createPatientCriticalitySettings()}
          >
            {!isCreatePatientCriticalityLoading && (
              <span className='indicator-label'>Create Settings</span>
            )}
            {isCreatePatientCriticalityLoading && (
              <span className='indicator-progress d-block'>
                creating...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </PrimaryButton>
        )}
      </div>

      <div className="card-body">
        {bloodPressureSettings?.length > 0 ? (
          <>
            {/* Navigation Tabs */}
            <ul className="nav nav-tabs nav-line-tabs nav-line-tabs-2x mb-5" role="tablist">
              <li className="nav-item" role="presentation">
                <a
                  className={`nav-link ${activeTab === 'systolic' ? 'active' : ''}`}
                  onClick={() => setActiveTab('systolic')}
                  role="tab"
                  style={{ cursor: 'pointer' }}
                >
                  <i className="fas fa-heartbeat me-2"></i>
                  Systolic Pressure
                </a>
              </li>
              <li className="nav-item" role="presentation">
                <a
                  className={`nav-link ${activeTab === 'diastolic' ? 'active' : ''}`}
                  onClick={() => setActiveTab('diastolic')}
                  role="tab"
                  style={{ cursor: 'pointer' }}
                >
                  <i className="fas fa-heart me-2"></i>
                  Diastolic Pressure
                </a>
              </li>
            </ul>

            {/* Tab Content */}
            <div className="tab-content">
              <TabContent type={activeTab} />
            </div>
          </>
        ) : (
          <div className="text-center py-5">
            <i className="fas fa-cog fa-3x text-muted mb-3"></i>
            <h5 className="text-muted">No Alert Settings Configured</h5>
            <p className="text-muted">Create alert settings to monitor patient vital signs and receive notifications when values fall outside normal ranges.</p>
          </div>
        )}
      </div>

      {/* Keep existing modals for backward compatibility */}
      {isEditCriticalitySettingModalOpen && (
        <EditCriticalitySettingsModal
          isOpen={isEditCriticalitySettingModalOpen}
          onClose={() => setIsEditCriticalitySettingModalOpen(false)}
          handleUpdate={handleSubmitSettings}
          currentValues={currentValues}
        />
      )}

      {openConfirmationModal && (
        <ConfirmationModal
          isOpen={openConfirmationModal}
          onConfirm={handleDeletePatientSettings}
          onCancel={() => setOpenConfirmationModal(false)}
          title="Delete Alert Settings"
          message="Are you sure you want to delete all alert settings for this patient?"
          confirmButtonTitle="Delete Settings"
        />
      )}
    </div>
  )
}

export default PatientAlertsSettings
